<script setup lang="ts">
import moment from 'moment';
import { Method } from '~/enums';
import { useEquipStore } from '~/store/equipStore';
import { useTocStore } from '~/store/tocStore';
import { useUserStore } from '~/store/userStore';

interface constructorAPI{
  method: Method,
  url: string,
  queryParams: string[],
  paramsSet: Object,
  params?: Object,
  query?: Object,
  body?: Object,
  setting?: any
}

const route = useRoute();
const router = useRouter();

const userStore = useUserStore();
const tocStore = useTocStore();
const equipStore = useEquipStore();

const props = defineProps<{
  api: constructorAPI,
  pageType: string,
  intervalTime?: {type: any, default: undefined},
  useBatchMode?: boolean // 배치 모드 사용 여부,
  property?: any,
  dynamicParams?: { parentDongCode?: string | null, groupLevel?: string | null } // 동적 파라미터
}>()

const intervalTime = ref();
const isApiEnabled = ref(true); // API 호출 활성화 상태
const isLoading = ref(false); // 로딩 상태
const hasInitialData = ref(false); // 초기 데이터 로드 여부
const hasInitialCall = ref(false); // 최초 API 호출 여부
const isCalling = ref(false); // API 호출 중인지 확인하는 상태
const callCount = ref(0); // API 호출 횟수 추적
const lastCallTime = ref(0); // 마지막 API 호출 시간
const callDebounceMs = 1000; // API 호출 디바운스 시간 (1초)
const lastApiRequest = ref(''); // 마지막 요청 키를 저장할 변수
const lastApiParams = ref<any>({}); // 마지막 파라미터를 저장할 변수
const retryCount = ref(0); // 재시도 횟수
const maxRetries = 3; // 최대 재시도 횟수
const activeRequests = new Set<number>(); // 동시 요청 추적

let requestSequence = 0;
let latestRequestToken = 0;

onUnmounted(() => {
  if(intervalTime.value){
    clearInterval(intervalTime.value);
  }
});

router.beforeEach((to, next) => {
  if(intervalTime.value){
    console.log('interval clear');
    clearInterval(intervalTime.value);
  }
})

const widgetApiData = ref();
const widgetAApiQuery = ref<any>({});
const cid = ref("");
const searchDateType = ref('0');
const startDate = ref(moment().format('YYYY-MM-DD'));
const endDate = ref(moment().format('YYYY-MM-DD'));
const eventType = ref('equip');
const searchDate = ref(moment().format('YYYY-MM-DD'));

// API 호출 활성화/비활성화
const enableApi = () => {
  isApiEnabled.value = true;
  // console.log('✅ API 호출 활성화됨');
  // 활성화 시 최초 호출이 안된 경우에만 API 호출
  if (!hasInitialCall.value) {
    callApi();
  }
};

const disableApi = () => {
  isApiEnabled.value = false;
  if(intervalTime.value){
    clearInterval(intervalTime.value);
  }
  // console.log('❌ API 호출 비활성화됨');
};

// 배치 API 호출을 위한 요청 준비 함수
function prepareApiRequest(parameter?: any) {
  const paramCid = parameter?.cid as string | undefined;
  cid.value = paramCid ?? tocStore.selectEquipInfo.cid;
  
  let url;
  if(props.api.url.includes("{cid}")){
    url = props.api.url.replace("{cid}", cid.value ?? '');
  } else if(props.api.url.includes("{siteId}")){
    url = props.api.url.replace("{siteId}", (tocStore.selectSiteInfo as any).siteId ?? '');
  } else {
    url = props.api.url;
  }
  
  // 쿼리 객체 초기화
  widgetAApiQuery.value = {};

  if(props.api.queryParams){
    for(let i = 0; i < props.api.queryParams.length; i++){
      if(props.api.queryParams[i] === 'searchDate'){
        widgetAApiQuery.value[props.api.queryParams[i]] = searchDate.value;
      } else {
        widgetAApiQuery.value[props.api.queryParams[i]] = (tocStore.selectSiteInfo as any)[props.api.queryParams[i]];
      }
      if(props.api.queryParams[i] === 'searchDateType'){
        widgetAApiQuery.value[props.api.queryParams[i]] = searchDateType.value;
      }
      if(props.api.queryParams[i] === 'startDate'){
        widgetAApiQuery.value[props.api.queryParams[i]] = startDate.value;
      }
      if(props.api.queryParams[i] === 'endDate'){
        widgetAApiQuery.value[props.api.queryParams[i]] = endDate.value;
      }
      if(props.api.queryParams[i] === 'eventType'){
        widgetAApiQuery.value[props.api.queryParams[i]] = eventType.value;
      }
      if(props.api.queryParams[i] === 'cid'){
        if(props.pageType === 'drgeo'){
          widgetAApiQuery.value[props.api.queryParams[i]] = tocStore.selectSiteInfo.cid;
        } else {
          widgetAApiQuery.value[props.api.queryParams[i]] = paramCid ?? equipStore.selectEquipInfo.cid;
        }
      }
      if(props.api.queryParams[i] === 'siteName'){
        widgetAApiQuery.value[props.api.queryParams[i]] = (tocStore.selectSiteInfo as any).siteName;
      }
    }
  }

  if(props.api.setting){
    const settingKey = Object.keys(props.api.setting);

    for(let i = 0; i < settingKey.length; i++){
      widgetAApiQuery.value[settingKey[i]] = checkParamsValue(props.api.setting[settingKey[i]]);
    }
  }

  if(parameter){
    const keys = Object.keys(parameter);
    for(let i = 0; i < keys.length; i++){
      widgetAApiQuery.value[keys[i]] = parameter[keys[i]];
    }
  }

  // 동적 파라미터 처리 (mapdashs API용)
  if(props.dynamicParams){
    // undefined 값도 포함하여 API 파라미터에 추가
    if(props.dynamicParams.parentDongCode !== null){
      widgetAApiQuery.value['parentDongCode'] = props.dynamicParams.parentDongCode;
    }
    if(props.dynamicParams.groupLevel !== null){
      widgetAApiQuery.value['groupLevel'] = props.dynamicParams.groupLevel;
    }
  }

  // 지역 코드 파라미터 처리 (ProdChart 등 지역 연동 API용)
  if(props.dynamicParams){
    const { parentDongCode, groupLevel } = props.dynamicParams;
    
    // console.log('🔍 BaseComponent 동적 파라미터 처리:', {
    //   url: url,
    //   parentDongCode,
    //   groupLevel,
    //   dynamicParams: props.dynamicParams
    // });
    
    if(parentDongCode && groupLevel) {
      // parentDongCode를 지역 코드로 변환
      if(groupLevel === 'sigungu') {
        // 시군구 레벨: parentDongCode는 10자리 시도 코드 (예: "1100000000")
        // cityCode: 2자리 (예: "11")
        widgetAApiQuery.value['cityCode'] = parentDongCode.substring(0, 2);
        // sigunguCode는 undefined로 설정 (시도 전체 데이터)
        widgetAApiQuery.value['sigunguCode'] = undefined;
        widgetAApiQuery.value['dongCode'] = undefined;
        // console.log('📍 시군구 레벨 설정:', {
        //   cityCode: widgetAApiQuery.value['cityCode'],
        //   sigunguCode: widgetAApiQuery.value['sigunguCode'],
        //   dongCode: widgetAApiQuery.value['dongCode']
        // });
      } else if(groupLevel === 'dong') {
        // 읍면동 레벨: parentDongCode는 10자리 시군구 코드 (예: "1111000000")
        // cityCode: 앞 2자리 (예: "11")
        // sigunguCode: 3~5번째 자리 (예: "110" - dongCode에서 3~5번째)
        widgetAApiQuery.value['cityCode'] = parentDongCode.substring(0, 2);
        widgetAApiQuery.value['sigunguCode'] = parentDongCode.substring(2, 5);
        // dongCode는 undefined로 설정 (시군구 전체 데이터)
        widgetAApiQuery.value['dongCode'] = undefined;
        // console.log('📍 읍면동 레벨 설정:', {
        //   cityCode: widgetAApiQuery.value['cityCode'],
        //   sigunguCode: widgetAApiQuery.value['sigunguCode'],
        //   dongCode: widgetAApiQuery.value['dongCode']
        // });
      }
    } else if(parentDongCode === null || parentDongCode === undefined) {
      // 전국 레벨: 모든 지역 코드를 undefined로 설정
      widgetAApiQuery.value['cityCode'] = undefined;
      widgetAApiQuery.value['sigunguCode'] = undefined;
      widgetAApiQuery.value['dongCode'] = undefined;
      // console.log('📍 전국 레벨 설정:', {
      //   cityCode: widgetAApiQuery.value['cityCode'],
      //   sigunguCode: widgetAApiQuery.value['sigunguCode'],
      //   dongCode: widgetAApiQuery.value['dongCode']
      // });
    }
  }

  return {
    method: props.api.method,
    url: url,
    query: widgetAApiQuery.value,
    body: props.api.body,
    params: props.api.params
  };
}

function extractRequestMeta(parameter?: Record<string, any>) {
  if (!parameter) {
    return {
      payload: undefined as Record<string, any> | undefined,
      requestId: undefined as number | undefined
    };
  }

  const cloned = { ...parameter } as Record<string, any>;
  let requestId: number | undefined;

  if (Object.prototype.hasOwnProperty.call(cloned, '_requestId')) {
    requestId = Number(cloned._requestId);
    delete cloned._requestId;
  }

  const payload = Object.keys(cloned).length ? cloned : undefined;
  return { payload, requestId };
}

async function callApi(parameter?: any){
  if (!isApiEnabled.value) {
    console.log('⚠️ API 호출이 비활성화되어 있습니다.');
    return;
  }

  const { payload: normalizedParams, requestId: explicitRequestId } = extractRequestMeta(parameter);
  const comparisonParams = normalizedParams ? { ...normalizedParams } : {};
  const skipDebounce = explicitRequestId !== undefined;

  // 디바운싱: 너무 빠른 연속 호출 방지 (개선된 로직)
  const now = Date.now();
  if (!skipDebounce && now - lastCallTime.value < callDebounceMs) {
    console.log('⚠️ 너무 빠른 연속 호출입니다. 디바운싱으로 방지합니다.');
    // 디바운싱된 호출을 큐에 추가하여 나중에 실행
    setTimeout(() => {
      if (isApiEnabled.value && activeRequests.size === 0) {
        console.log('🔄 디바운싱된 API 호출 실행');
        callApi(parameter);
      }
    }, callDebounceMs - (now - lastCallTime.value) + 100); // 추가 100ms 여유
    return;
  }

  // API 요청 정보 생성
  const apiRequest = prepareApiRequest(normalizedParams);
  const requestKey = `${apiRequest.url}_${JSON.stringify(apiRequest.query || {})}_${JSON.stringify(apiRequest.params || {})}`;
  const skipDuplicateCheck = explicitRequestId !== undefined;
  
  // 동일한 API 요청인지 확인 (URL + 파라미터 기준) - 개선된 검사
  if (!skipDuplicateCheck &&
      lastApiRequest.value === requestKey && 
      JSON.stringify(lastApiParams.value) === JSON.stringify(comparisonParams) &&
      now - lastCallTime.value < callDebounceMs * 2) { // 시간 범위 단축 (2초)
    // console.log('⚠️ 동일한 API 요청이 너무 빠르게 연속 호출되었습니다. 중복 호출을 방지합니다.');
    // console.log(`🔍 요청 키: ${requestKey}`);
    // console.log(`⏰ 마지막 호출 후 경과 시간: ${now - lastCallTime.value}ms`);
    
    // 동일한 요청이지만 시간이 좀 지났다면 강제로 재호출
    if (now - lastCallTime.value > callDebounceMs) {
      console.log('🔄 동일한 요청이지만 시간이 지나서 강제 재호출합니다.');
      // 강제 재호출을 위해 마지막 호출 시간을 업데이트하지 않고 진행
    } else {
      return;
    }
  }

  if(intervalTime.value){
    clearInterval(intervalTime.value);
  }

  const requestToken = explicitRequestId ?? ++requestSequence;
  latestRequestToken = requestToken;
  activeRequests.add(requestToken);
  isCalling.value = true; // API 호출 시작 표시
  isLoading.value = true;

  try {
    callCount.value++; // 호출 횟수 증가
    lastCallTime.value = now; // 마지막 호출 시간 업데이트
    lastApiRequest.value = requestKey; // 마지막 요청 키 저장
    lastApiParams.value = comparisonParams; // 마지막 파라미터 저장
    
    // console.log(`🚀 API 호출 시작 (${callCount.value}번째): ${apiRequest.url}`);
    // console.log(`🔍 요청 키: ${requestKey}`);
    // console.log(`⏰ 호출 시간: ${new Date(now).toISOString()}`);


    // console.log('apiRequest: ', apiRequest);

    
    // undefined 값들을 제거한 쿼리 파라미터 생성
    const cleanQuery = Object.fromEntries(
      Object.entries(apiRequest.query || {}).filter(([_, value]) => value !== undefined && value !== null)
    );
    
    // console.log('🧹 정리된 쿼리 파라미터:', cleanQuery);
    
    // 비동기 API 호출 - 화면 렌더링을 차단하지 않음
    const res = await useCustomFetch(apiRequest.url, {
      method: apiRequest.method,
      query: apiRequest.url.includes('toc/mapdashs') ? {
        parentDongCode: apiRequest.query.parentDongCode,
        groupLevel: apiRequest.query.groupLevel
      } : cleanQuery,
      body: apiRequest.body,
      params: apiRequest.params,
      // oFetch 자동 재시도 완전 비활성화
      retry: 0,
      retryDelay: 0
    });
    
    // HTML 응답 확인
    if (res.error.value) {
      const errorMessage = res.error.value?.message || '';
      if (errorMessage === 'SERVER_RELOADING') {
        // console.log('🔄 서버 재시작 감지. 잠시 후 재시도합니다.');
        // 5초 후 재시도
        setTimeout(() => {
          if (isApiEnabled.value) {
            console.log('🔄 서버 재시작 후 API 재시도');
            callApi(parameter);
          }
        }, 1000);
        return;
      }
      throw res.error.value;
    }

    // console.log("property: ", props.property, "로딩끝");

    if (requestToken === latestRequestToken) {
      widgetApiData.value = res.data.value;
      hasInitialData.value = true;
      hasInitialCall.value = true; // 최초 API 호출 완료 표시
      retryCount.value = 0; // 성공 시 재시도 횟수 리셋
      // console.log(`✅ API 호출 완료 (${callCount.value}번째): ${apiRequest.url}`, res.data.value);
    } else {
      console.log('⚠️ 최신 요청이 아니므로 응답을 무시합니다.', requestToken, latestRequestToken);
    }
   
    if(props.intervalTime && (props.intervalTime as any).time){
      intervalTime.value = setInterval(() => {
        // console.log('intervalTime');
        callApi(parameter)
     }, (props.intervalTime as any).time * 1000);
    }
  } catch (error: any) {
    // console.error('❌ API 호출 중 오류 발생:', error);
    
    // HTML 응답 오류 처리 (개선된 재시도 로직)
    if (error?.message === 'SERVER_RELOADING') {
      // console.log('🔄 서버 재시작 감지. 잠시 후 재시도합니다.');
      retryCount.value++;
      
      if (retryCount.value <= maxRetries) {
        const retryDelay = Math.min(1000 * retryCount.value, 5000); // 최대 5초
        setTimeout(() => {
          if (isApiEnabled.value) {
            // console.log(`🔄 서버 재시작 후 API 재시도 (${retryCount.value}/${maxRetries})`);
            callApi(parameter);
          }
        }, retryDelay);
        return;
      } else {
        // console.error('❌ 최대 재시도 횟수 초과. API 호출을 중단합니다.');
        retryCount.value = 0; // 재시도 횟수 리셋
      }
    }
    
    // 기타 오류 처리 (개선된 재시도 로직)
    if (error?.status === 500 || error?.status === 502 || error?.status === 503) {
      // console.log('🔄 서버 오류 발생. 잠시 후 재시도합니다.');
      retryCount.value++;
      
      if (retryCount.value <= maxRetries) {
        const retryDelay = Math.min(2000 * retryCount.value, 10000); // 최대 10초
        setTimeout(() => {
          if (isApiEnabled.value) {
            // console.log(`🔄 서버 오류 후 API 재시도 (${retryCount.value}/${maxRetries})`);
            callApi(parameter);
          }
        }, retryDelay);
        return;
      } else {
        // console.error('❌ 최대 재시도 횟수 초과. API 호출을 중단합니다.');
        retryCount.value = 0; // 재시도 횟수 리셋
      }
    }
    
    // 일반적인 네트워크 오류에 대한 폴백 메커니즘
    if (retryCount.value < maxRetries && !error?.message?.includes('SERVER_RELOADING')) {
      retryCount.value++;
      const retryDelay = Math.min(1000 * retryCount.value, 3000); // 최대 3초
      // console.log(`🔄 네트워크 오류로 인한 API 재시도 (${retryCount.value}/${maxRetries})`);
      
      setTimeout(() => {
        if (isApiEnabled.value) {
          callApi(parameter);
        }
      }, retryDelay);
      return;
    }
    
    // console.error(`❌ API 호출 실패 (${callCount.value}번째): ${apiRequest.url}`, error);
    retryCount.value = 0; // 최종 실패 시 재시도 횟수 리셋
  } finally {
    activeRequests.delete(requestToken);
    if (activeRequests.size === 0) {
      isLoading.value = false;
      isCalling.value = false;
      // 상태 초기화 보장을 위한 추가 체크
      setTimeout(() => {
        if (activeRequests.size === 0) {
          isCalling.value = false;
        }
      }, 1000);
    } else {
      isLoading.value = true;
      isCalling.value = true;
    }
  }
}

// 배치 API 호출을 위한 함수 (외부에서 호출)
async function callBatchApi(parameter?: any) {
  if(intervalTime.value){
    clearInterval(intervalTime.value);
  }

  const apiRequest = prepareApiRequest(parameter);
  
  // 배치 API 호출을 위해 요청 객체 생성
  const batchRequest = {
    ...apiRequest,
    id: `${props.pageType}_${props.api.url}` // 고유 식별자
  };

  return batchRequest;
}

function checkParamsValue(param: string){
  switch(param){  
    case "now":         return moment().format('YYYY-MM-DD');
    case "startMonth":  return moment().startOf('month').format('YYYY-MM-DD');
    case "endMonth":    return moment().endOf('month').format('YYYY-MM-DD');
    case "startYear":   return moment().startOf('year').format('YYYY-MM-DD');
    case "endYear":     return moment().endOf('year').format('YYYY-MM-DD');
    case "siteName":    return (tocStore.selectSiteInfo as any).siteName;
    case "cid"    :     return equipStore.selectEquipInfo.cid;

    default:  return param;
  }
}

await callApi();

// 동적 파라미터 변경 감지하여 API 재호출
watch(() => props.dynamicParams, (newParams, oldParams) => {
  if (newParams && JSON.stringify(newParams) !== JSON.stringify(oldParams)) {
    // console.log('동적 파라미터 변경 감지, API 재호출:', newParams);
    callApi(newParams);
  }
}, { deep: true });

// 외부에서 사용할 수 있도록 함수 노출
defineExpose({
  callApi,
  callBatchApi,
  prepareApiRequest,
  enableApi,
  disableApi,
  isApiEnabled: readonly(isApiEnabled),
  isLoading: readonly(isLoading),
  hasInitialData: readonly(hasInitialData),
  hasInitialCall: readonly(hasInitialCall),
  isCalling: readonly(isCalling),
  callCount: readonly(callCount)
});
</script>

<template>
  <slot 
    :apiData="widgetApiData" 
    :isApiEnabled="isApiEnabled" 
    :isLoading="isLoading"
    :hasInitialData="hasInitialData"
    @callApi="callApi"
  ></slot>
</template>
